import React, { useState, useRef } from 'react';
import { Upload, File, X, CheckCircle, /* AlertCircle, */ Dna, Download } from 'lucide-react';
import { toast } from 'react-toastify';
import { encodeFile } from '../services/api';
import { sanitizeDataName, isInputSafe, isFileTypeSafe, isFileSizeValid } from '../utils/security';

interface UploadState {
  file: File | null;
  dataName: string;
  isUploading: boolean;
  uploadProgress: number;
  uploadResult: any | null;
  encryptedKeyFile: {
    fileName: string;
    content: string;
    mimeType: string;
  } | null;
}

// Global state for last uploaded key file
let lastUploadedKeyFile: {
  fileName: string;
  content: string;
  mimeType: string;
} | null = null;

const StoneEngraveLoader: React.FC = () => (
  <span className="engraving-loader" aria-hidden="true">
    <span className="engraving-stone" />
    <span className="engraving-chisel" />
    <span className="engraving-dust" />
  </span>
);

const UploadPage: React.FC = () => {
  const [state, setState] = useState<UploadState>({
    file: null,
    dataName: '',
    isUploading: false,
    uploadProgress: 0,
    uploadResult: null,
    encryptedKeyFile: null,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Enhanced file validation using security utilities
      if (!file) {
        toast.error('No file selected');
        return;
      }
      
      if (!isFileSizeValid(file)) {
        toast.error('File size too large. Maximum size is 100GB.');
        return;
      }
      
      if (!isFileTypeSafe(file)) {
        toast.error('File type not allowed for security reasons.');
        return;
      }
      
      // Sanitize data name for security
      const sanitizedDataName = sanitizeDataName(file.name.split('.')[0]);
      
      setState(prev => ({
        ...prev,
        file,
        dataName: sanitizedDataName, // Use sanitized filename without extension as default
        uploadResult: null,
        encryptedKeyFile: null,
      }));
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    // Use passive: false to allow preventDefault
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent) => {
    // Use passive: false to allow preventDefault
    event.preventDefault();
    event.stopPropagation();
    
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Enhanced file validation using security utilities
      if (!file) {
        toast.error('No file selected');
        return;
      }
      
      if (!isFileSizeValid(file)) {
        toast.error('File size too large. Maximum size is 100GB.');
        return;
      }
      
      if (!isFileTypeSafe(file)) {
        toast.error('File type not allowed for security reasons.');
        return;
      }
      
      // Sanitize file name for security
      const sanitizedDataName = sanitizeDataName(file.name.split('.')[0]);
      
      setState(prev => ({
        ...prev,
        file,
        dataName: sanitizedDataName,
        uploadResult: null,
        encryptedKeyFile: null,
      }));
    }
  };

  const handleUpload = async () => {
    if (!state.file || !state.dataName.trim()) {
      toast.error('Please select a file and enter a data name');
      return;
    }

    // Validate input security
    if (!isInputSafe(state.dataName)) {
      toast.error('Invalid characters detected in data name. Please use only letters, numbers, and basic punctuation.');
      return;
    }

    setState(prev => ({ ...prev, isUploading: true, uploadProgress: 0 }));

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setState(prev => ({
          ...prev,
          uploadProgress: Math.min(prev.uploadProgress + 10, 90),
        }));
      }, 200);

      const result = await encodeFile(state.file, state.dataName.trim());
      
      clearInterval(progressInterval);
      
      // // Debug logging (removed sensitive data exposure)
      // console.log('Upload result:', {
      //   success: result.success,
      //   dataId: result.dataId,
      //   dataName: result.dataName,
      //   message: result.message,
      //   // Note: encryptedKeyFile content removed for security
      //   hasKeyFile: !!result.encryptedKeyFile
      // });
      
      setState(prev => ({
        ...prev,
        isUploading: false,
        uploadProgress: 100,
        uploadResult: result,
        encryptedKeyFile: result.encryptedKeyFile || null,
      }));

      toast.success(`File "${state.file.name}" successfully encoded and stored! Key file will be downloaded automatically.`);
      
      // Auto-download the key file immediately
      if (result.encryptedKeyFile) {
        // Store globally for fallback download
        lastUploadedKeyFile = result.encryptedKeyFile;
        
        // Use the result directly instead of waiting for state update
        setTimeout(() => {
          try {
            // Convert base64 content to blob
            const byteCharacters = atob(result.encryptedKeyFile.content);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'text/plain' });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = result.encryptedKeyFile.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Key file downloaded successfully!');
          } catch (error) {
            // console.error('Error downloading key file:', error);
            toast.error('Failed to download key file automatically');
          }
        }, 1500); // Slightly longer delay to ensure everything is ready
      }
      
      // Auto-reset form after key file download
      setTimeout(() => {
        setState({
          file: null,
          dataName: '',
          isUploading: false,
          uploadProgress: 0,
          uploadResult: null,
          encryptedKeyFile: null,
        });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 8000); // Reset after 8 seconds to allow time for download

    } catch (error: any) {
      // console.error('Upload error:', error);
      setState(prev => ({ ...prev, isUploading: false, uploadProgress: 0 }));
      
      let errorMessage = 'Upload failed. Please try again.';
      if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timeout. The file might be too large. Please try a smaller file.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setState({
      file: null,
      dataName: '',
      isUploading: false,
      uploadProgress: 0,
      uploadResult: null,
      encryptedKeyFile: null,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadEncryptedKeyFile = () => {
    if (!state.encryptedKeyFile) return;

    try {
      // Convert base64 content to blob
      const byteCharacters = atob(state.encryptedKeyFile.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'text/plain' });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = state.encryptedKeyFile.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Encrypted key file downloaded successfully!');
    } catch (error) {
      // console.error('Error downloading key file:', error);
      toast.error('Failed to download encrypted key file');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    if (fileType.startsWith('audio/')) return '🎵';
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('text')) return '📝';
    return '📁';
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-6">
        <h1 className="text-3xl font-bold text-gold mb-3 flex items-center justify-center gap-3">
          <Dna size={32} />
          Upload & Encode Files
        </h1>
        <p className="text-lg text-gray-300">
          Upload any file type and engrave it into DNA sequences.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-bold text-gold flex items-center gap-2">
              <Upload size={24} />
              File Upload
            </h2>
          </div>

          {/* File Drop Zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              state.file
                ? 'border-gold bg-gold bg-opacity-10'
                : 'border-gray-600 hover:border-gold'
            }`}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {state.file ? (
              <div className="space-y-3">
                <div className="text-3xl">{getFileIcon(state.file.type)}</div>
                <div>
                  <p className="text-gold font-semibold">{state.file.name}</p>
                  <p className="text-sm text-gray-400">
                    {formatFileSize(state.file.size)} • {state.file.type}
                  </p>
                </div>
                <button
                  onClick={() => setState(prev => ({ ...prev, file: null }))}
                  className="text-red-400 hover:text-red-300 flex items-center gap-1 mx-auto"
                >
                  <X size={16} />
                  Remove
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <Upload size={40} className="text-gold mx-auto" />
                <div>
                  <p className="text-lg font-semibold text-gold">
                    Drop your file here
                  </p>
                  <p className="text-gray-400">or click to browse</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept="*/*"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-primary"
                >
                  Choose File
                </button>
              </div>
            )}
          </div>

          {/* Data Name Input */}
          <div className="mt-4">
            <label className="block text-sm font-semibold text-gold mb-2">
              Data Name (for identification)
            </label>
            <input
              type="text"
              value={state.dataName}
              onChange={(e) => {
                const sanitizedValue = sanitizeDataName(e.target.value);
                setState(prev => ({ ...prev, dataName: sanitizedValue }));
              }}
              placeholder="Enter a name for this data..."
              className="input"
              disabled={state.isUploading}
              maxLength={50}
            />
          </div>

          {/* Upload Button */}
          <div className="mt-4 flex gap-3">
            <button
              onClick={handleUpload}
              disabled={!state.file || !state.dataName.trim() || state.isUploading}
              className="btn btn-primary flex-1 flex items-center justify-center gap-2"
            >
              {state.isUploading ? (
                <>
                  <StoneEngraveLoader />
                  <span>Engraving... {state.uploadProgress}%</span>
                </>
              ) : (
                <>
                  <Dna size={20} />
                  Engrave to DNA
                </>
              )}
            </button>
            
            {state.file && (
              <button
                onClick={resetForm}
                className="btn"
                disabled={state.isUploading}
              >
                Reset
              </button>
            )}
            
            {state.encryptedKeyFile && (
              <button
                onClick={resetForm}
                className="btn btn-secondary"
                disabled={state.isUploading}
              >
                Upload Another File
              </button>
            )}
          </div>

          {/* Progress Bar */}
          {state.isUploading && (
            <>
              <div className="mt-4">
                <div className="bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-gold h-2 rounded-full transition-all duration-300"
                    style={{ width: `${state.uploadProgress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-400 mt-2 text-center">
                  Engraving file to DNA sequence...
                </p>
              </div>
              <div className="flex items-center gap-3 mt-3 text-sm text-gray-400">
                <StoneEngraveLoader />
                <span>Stone engraving in progress...</span>
              </div>
            </>
          )}
        </div>

        {/* Supported Formats & Info */}
        <div className="space-y-4">
          {/* Supported Formats */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-bold text-gold">Supported File Types</h3>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                'Images: JPG, PNG, GIF, WebP',
                'Videos: MP4, AVI, WebM',
                'Audio: MP3, WAV, AAC',
                'Documents: PDF, DOC, DOCX',
                'Text: TXT, JSON, CSV',
                'Archives: ZIP, RAR, 7Z',
                'Code: JS, TS, PY, HTML',
                'Any other file type'
              ].map((format, index) => (
                <div key={index} className="flex items-center gap-2 text-gray-300">
                  <CheckCircle size={16} className="text-green-400" />
                  {format}
                </div>
              ))}
            </div>
          </div>

          {/* Upload Result */}
          {state.uploadResult && (
            <div className="card border-green-500">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle size={24} className="text-green-400" />
                <h3 className="text-lg font-bold text-green-400">Upload Successful!</h3>
              </div>
              <div className="space-y-2 text-sm">
                <p><span className="text-gold">Data ID:</span> {state.uploadResult.dataId}</p>
                <p><span className="text-gold">Data Name:</span> {state.uploadResult.dataName}</p>
                <p><span className="text-gold">Message:</span> {state.uploadResult.message}</p>
                {state.encryptedKeyFile ? (
                  <p className="text-yellow-400 text-xs">
                    🔐 Key file ready for download below
                  </p>
                ) : (
                  <p className="text-red-400 text-xs">
                    ⚠️ Key file not received from server
                  </p>
                )}
              </div>
              
              {/* Encrypted Key File Download */}
              {state.encryptedKeyFile ? (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <Dna size={20} className="text-gold" />
                    <h4 className="text-md font-bold text-gold">🔐 Encrypted Key File Generated</h4>
                  </div>
                  <div className="bg-green-900 bg-opacity-20 border border-green-500 rounded-lg p-3 mb-3">
                    <p className="text-sm text-green-200 mb-2">
                      ✅ <strong>Auto-Download:</strong> Key file will be downloaded automatically in a moment. Save it securely!
                    </p>
                    <p className="text-xs text-green-300">
                      File: {state.encryptedKeyFile.fileName}
                    </p>
                  </div>
                  <button
                    onClick={downloadEncryptedKeyFile}
                    className="btn btn-primary flex items-center gap-2 w-full"
                  >
                    <Download size={16} />
                    Download Key File Now
                  </button>
                </div>
              ) : (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="bg-red-900 bg-opacity-20 border border-red-500 rounded-lg p-3">
                    <p className="text-sm text-red-200 mb-2">
                      ⚠️ <strong>Issue:</strong> Key file was not received from the server. This might be a backend issue.
                    </p>
                    <p className="text-xs text-red-300">
                      Check the browser console for error details.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Persistent Key File Download Section */}
      {lastUploadedKeyFile && (
        <div className="mt-8">
          <div className="card border-green-500 bg-green-900 bg-opacity-10">
            <div className="flex items-center gap-2 mb-4">
              <Dna size={24} className="text-green-400" />
              <h3 className="text-lg font-bold text-green-400">🔐 Last Generated Key File</h3>
            </div>
            <div className="bg-green-900 bg-opacity-20 border border-green-500 rounded-lg p-3 mb-3">
              <p className="text-sm text-green-200 mb-2">
                ✅ <strong>Key file available:</strong> Download the key file for your last encoded file.
              </p>
              <p className="text-xs text-green-300">
                File: {lastUploadedKeyFile.fileName}
              </p>
            </div>
            <button
              onClick={() => {
                if (!lastUploadedKeyFile) return;
                try {
                  // Convert base64 content to blob
                  const byteCharacters = atob(lastUploadedKeyFile.content);
                  const byteNumbers = new Array(byteCharacters.length);
                  for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                  }
                  const byteArray = new Uint8Array(byteNumbers);
                  const blob = new Blob([byteArray], { type: 'text/plain' });

                  // Create download link
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = lastUploadedKeyFile.fileName;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  window.URL.revokeObjectURL(url);

                  toast.success('Key file downloaded successfully!');
                } catch (error) {
                  // console.error('Error downloading key file:', error);
                  toast.error('Failed to download key file');
                }
              }}
              className="btn btn-primary flex items-center gap-2 w-full mt-4 mb-4"
            >
              <Download size={16} />
              Download Key File
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadPage;